import { NIL } from 'uuid';
import { hidesLeadFromViewer } from '../../functions/leadbotSession.functions';
import { eLeadbotState } from '../../pages/MessengerV2/Messenger.enum';
import { shouldRemoveLeadFromFunnel } from '../../pages/Pipe/Pipe.functions';
import { eChannelType } from '../../providers/SocketsProvider/Sockets.enums';
import { eUserRole } from '../../providers/UserProvider/User.enums';
import { isValidUUID } from '../../utils/isValidUUID';
import { RouterPaths } from '../Router/Router.constants';

/** What a link to a lead's conversation carries (AGI-761). */
export type tLeadChatTarget = {
  leadId: string;
  integration?: eChannelType;
};

const CHANNELS: ReadonlySet<string> = new Set(Object.values(eChannelType));

// bff-web answers the nil UUID for a lead with no funnel (inbox).
const hasFunnel = (funnelId: string | null | undefined) =>
  !!funnelId && funnelId !== NIL;

/**
 * The one URL for a lead's conversation: `/workspace/chat?leadId=…[&integration=…]`.
 * It is what a modifier click, a new tab or a copied link opens; a plain click
 * opens the chat popup instead (`LeadChatLink`).
 */
export function buildLeadChatUrl(target: tLeadChatTarget): string {
  const params = new URLSearchParams({ leadId: target.leadId });
  if (target.integration) params.set('integration', target.integration);
  return `${RouterPaths.chat}?${params.toString()}`;
}

/**
 * Reads a conversation link back, or null when `href` is not one: another
 * origin, another path, a lead id that is not a UUID. An unknown `integration`
 * is dropped rather than trusted, so the chat falls back to its default channel.
 */
export function parseLeadChatUrl(
  href: string | null | undefined,
  origin: string,
): tLeadChatTarget | null {
  if (!href) return null;
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || url.pathname !== RouterPaths.chat) return null;

  const leadId = url.searchParams.get('leadId') ?? '';
  if (!isValidUUID(leadId)) return null;

  const integration = url.searchParams.get('integration');
  return integration && CHANNELS.has(integration)
    ? { leadId, integration: integration as eChannelType }
    : { leadId };
}

/**
 * Can this viewer see the lead? The same two rules that hide a lead from the
 * HTTP lists: the Lead Agent's hold (non-superusers) and a Seller's private
 * funnel assigned to someone else. Opening a single lead is not filtered by
 * the backend, so the popup asks this before it mounts the chat.
 */
export function canViewLeadChat(params: {
  currentUserRole: eUserRole;
  currentUserId: string | undefined;
  leadbotState: eLeadbotState | null | undefined;
  assigneeAuthId: string | null | undefined;
  funnelId: string | null | undefined;
  funnelIsPublic: boolean | null | undefined;
}): boolean {
  return (
    !hidesLeadFromViewer({
      currentState: params.leadbotState,
      currentUserRole: params.currentUserRole,
    }) &&
    !shouldRemoveLeadFromFunnel({
      currentUserRole: params.currentUserRole,
      currentUserId: params.currentUserId,
      assignedUserId: params.assigneeAuthId || null,
      funnelIsPublic: params.funnelIsPublic,
      leadHasFunnel: hasFunnel(params.funnelId),
    })
  );
}

/**
 * Whether answering `canViewLeadChat` needs the funnel's privacy. Only a Seller
 * looking at a lead assigned to someone else does; everyone else is decided
 * without fetching the funnel.
 */
export function needsFunnelPrivacy(params: {
  currentUserRole: eUserRole;
  currentUserId: string | undefined;
  assigneeAuthId: string | null | undefined;
  funnelId: string | null | undefined;
}): boolean {
  return (
    params.currentUserRole === eUserRole.SELLER &&
    hasFunnel(params.funnelId) &&
    !!params.assigneeAuthId &&
    params.assigneeAuthId !== params.currentUserId
  );
}

/** A plain primary click, the only one the popup takes over from the browser. */
export function isPlainLeftClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}
